import React, { useState } from 'react'

import { MY_TEAMS_ADVISOR } from '@zet/constants'
import {
  B4,
  BTN_SM,
  Button,
  colors,
  Icon,
  SH3,
  SH4,
  Stack,
  XStack,
} from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import { APP_NAME } from '@zet/end-points'
import { useExperiments } from '@zet/experiments'
import { useBrandDetailsById } from '@zet/feature-brands/api/brands/hooks'
import { IEligibleCustomerCard } from '@zet/feature-brands/api/brands/types'
import { useCreditCardCrossSelling } from '@zet/feature-brands/api/crossSelling/hooks'
import CrossSellingBottomSheet from '@zet/feature-brands/screens/brandDetails/creditCardCrossSelling/crossSellingBottomSheet'
import { getApprovalChanceColor } from '@zet/feature-brands/utils'
import { useTier } from '@zet/feature-loyalty/api/hooks'
import { i18n } from '@zet/localization'

import useEligibleCustomer from './useEligibleCustomer'

const EligibleCustomerCard = ({
  customerData,
  isHold,
  source,
  brandId,
  brandCategory,
  brandName,
  shareClickEventPayload,
  onCallClickEventPayload,
  routeName,
}: IEligibleCustomerCard) => {
  const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false)
  const { data: experiment } = useExperiments()
  const { data: getTier } = useTier()
  const { handlePress, eventPayload, handleCallPress } = useEligibleCustomer({
    customerData,
    source,
    brandId,
    brandCategory,
    brandName,
    shareClickEventPayload,
    routeName,
  })

  const { data: creditCardCrossSelling } = useCreditCardCrossSelling(
    customerData?.customerId,
    +brandId
  )
  const partnerId = creditCardCrossSelling?.partner?.partnerId
  const { data: brandContent } = useBrandDetailsById(partnerId || '')
  const crossSellingBrandName = brandContent?.name

  const onClick = () => {
    if (
      creditCardCrossSelling?.partner &&
      experiment?.UI_isCreditCardCrossSelling &&
      APP_NAME !== MY_TEAMS_ADVISOR
    ) {
      setIsBottomSheetVisible(true)
    } else {
      handlePress()
    }
  }

  return (
    <Stack className="py-4/2 border-grays-40 mb-3 rounded border bg-white px-3">
      <XStack className="items-center justify-between">
        <Stack>
          <SH3 className="text-grays-100">{customerData?.fullName}</SH3>
          <B4 className="text-grays-60 pt-1">
            +91-{customerData?.phoneNumber}
          </B4>
        </Stack>
        <Pressable
          className="border-primary-30 rounded-md border p-2"
          onPress={handleCallPress}
          eventName="click_action"
          eventPayload={
            onCallClickEventPayload || {
              item_name: `${customerData?.phoneNumber}_call_customer`,
            }
          }
        >
          <Icon type="PhoneFilled" color={colors.primary[100]} />
        </Pressable>
      </XStack>
      {customerData?.approvalRate && (
        <SH4
          className={`${getApprovalChanceColor(
            customerData?.textColor as string
          )} mb-0 mt-2`}
        >
          {i18n.t('recommendationCard.approvalChance', {
            initialText: customerData?.approvalRate,
          })}
        </SH4>
      )}
      {APP_NAME !== MY_TEAMS_ADVISOR && customerData?.rejectionPayout ? (
        <XStack className="border-grays-40 my-3 items-center space-x-2 border-y border-dotted py-2">
          <Stack>
            <Icon
              type="RupeeCircleOutlined"
              size={20}
              color={colors.error[100]}
            />
          </Stack>
          <SH4 className="text-primary-200">
            {i18n.t('eligiblecustomers.rejectionMsg', {
              amount: customerData?.rejectionPayout,
            })}
          </SH4>
        </XStack>
      ) : (
        <Stack className="border-grays-30 my-3 items-center space-x-2 border" />
      )}
      <Button
        className="bg-primary-10"
        fullWidth
        disabled={isHold}
        eventName="click_action"
        eventPayload={eventPayload}
        onPress={onClick}
      >
        <XStack className="items-center justify-center space-x-2">
          <Icon type="Share" color={colors.primary[100]} />
          <BTN_SM className="text-primary-100">
            {i18n.t('eligiblecustomers.cta')}
          </BTN_SM>
        </XStack>
      </Button>
      {isBottomSheetVisible && (
        <CrossSellingBottomSheet
          isBottomSheetVisible={isBottomSheetVisible}
          setIsBottomSheetVisible={setIsBottomSheetVisible}
          customerName={customerData?.fullName}
          crossSellingData={creditCardCrossSelling}
          crossSellingBrandName={crossSellingBrandName}
          customerId={customerData?.customerId}
          customerPhoneNumber={customerData?.phoneNumber}
          isEligibleCard={true}
          currentBrandName={brandName}
          handlePress={handlePress}
          tier={getTier?.cohort}
          extraPayoutPercentage={getTier?.extraPayoutPercentage}
        />
      )}
    </Stack>
  )
}

function arePropsEqual(
  prevProps: IEligibleCustomerCard,
  nextProps: IEligibleCustomerCard
) {
  return (
    prevProps?.customerData?.customerId === nextProps?.customerData?.customerId
  )
}

export default React.memo(EligibleCustomerCard, arePropsEqual)
